import type { FC } from 'react';

import { Text } from '@opendoor/bricks-next';
import type { TextProps } from '@opendoor/bricks-next';
import type { FormatCurrencyOptions } from 'shared/helpers/formatters';
import { formatCurrency } from 'shared/helpers/formatters';
import { hasValue } from 'shared/helpers/globals';

/**
 * base props for the component
 */
type CommonBaseProps = {
  /**
   * The currency value to format - defaults to cents.
   * If dollars are passed, set `inDollars`.
   */
  value: number | null;

  /**
   * Can be a `p` or a `span` element, defaults to `p`.
   */
  tag?: 'p' | 'span';
};

/**
 * disallow children or the default tag options
 */
type ReducedTextProps = Omit<TextProps, 'children' | 'tag'>;

/**
 * stripSigil is only provided when not using split-sigil mode,
 * otherwise options are the same as formatCurrency
 */
type BaseFormatProps = Omit<FormatCurrencyOptions, 'stripSigil'>;

/**
 * shared combination of currency props used by both usage types
 */
type BaseCurrencyProps = CommonBaseProps & BaseFormatProps & ReducedTextProps;

/**
 * props describing un-split sigil (or strippable) mode
 */
type StripCurrencyProps = BaseCurrencyProps & {
  /**
   * Removes the currency sign if true
   */
  stripSigil?: boolean;
};

/**
 * props describing split-sigil mode
 */
type SplitCurrencyProps = BaseCurrencyProps & {
  /**
   * When this prop is set, the Currency component operates in split-sigil mode.
   * These are properties applied to the currency sign.
   *
   * The base properties are applied to the numeric value and the outer element.
   *
   * The sigil will inherit the base color unless overridden.
   *
   * The `tag` prop is always `span`.
   */
  sigilProps: ReducedTextProps;
};

/**
 * all possible props for the Currency component
 */
type CurrencyProps = StripCurrencyProps | SplitCurrencyProps;

/**
 * Renders a formatted currency value as a `Text` element.
 *
 * In simple mode (default), outputs a single `Text` with the formatted string.
 *
 * In split-sigil mode (`split-sigil`), renders the "$" in an independently styled
 * `Text` span — useful when the sigil needs different typography or spacing.
 *
 * When using split-sigil mode the top-level props are applied to the numeric portion,
 * while the currency symbol is formatted by the `sigilProps` prop.
 *
 * One additional exception is that the sigil inherits the top-level color unless
 * overridden.
 */
export const Currency: FC<CurrencyProps> = ({
  value,
  showCents,
  inDollars,
  negate,
  fallback,
  tag,
  ...restProps
}) => {
  const formatOptions: BaseFormatProps = {
    showCents,
    inDollars,
    negate,
    ...(fallback ? { fallback } : undefined),
  };

  if (hasValue(value)) {
    if ('sigilProps' in restProps) {
      const { sigilProps, ...outerProps } = restProps;

      return (
        <Text tag={tag ?? 'p'} {...outerProps}>
          <Text
            tag="span"
            {...(restProps.color ? { color: restProps.color } : undefined)}
            {...sigilProps}
          >
            $
          </Text>
          {formatCurrency(value, {
            ...formatOptions,
            stripSigil: true,
          })}
        </Text>
      );
    }
  }

  const { stripSigil, textProps } = getSimpleProps(restProps);
  return (
    <Text tag={tag ?? 'p'} {...textProps}>
      {formatCurrency(value, {
        ...formatOptions,
        stripSigil,
      })}
    </Text>
  );
};

type RemovedProps = keyof CommonBaseProps | keyof BaseFormatProps;
type TrimmedSplitProps = Omit<SplitCurrencyProps, RemovedProps>;
type TrimmedStripProps = Omit<StripCurrencyProps, RemovedProps>;
type TrimmedProps = TrimmedSplitProps | TrimmedStripProps;

const getSimpleProps = (props: TrimmedProps) => {
  let textProps: ReducedTextProps;
  let stripSigil: boolean | undefined;
  let _sigilProps: ReducedTextProps | undefined;

  if ('sigilProps' in props) {
    ({ sigilProps: _sigilProps, ...textProps } = props);
  } else {
    ({ stripSigil, ...textProps } = props);
  }

  return { stripSigil, textProps };
};
